import Grid from '@mui/material/Grid2';
import { Box, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { toast } from 'material-react-toastify';
import { ProductSummary } from '../../../interfaces/Product/ProductSummary';
import { ProductPopularity } from '../../../interfaces/Product/ProductPopularity';
import { ComponentStyle } from '../../../interfaces/Settings/SettingsV2/ComponentStyle';
import { useThemeColor } from '../hooks/useThemeColor';
import { getProductsByPopularity } from '../../../api/ProductSummary';
import { ProductCardPlaceholder } from './ProductCardPlaceholder';
import { ProductCard } from './ProductCard';
import { EditableStyleWrapper } from '../StyleEditor/EditableStyleWrapper';

const getTitleStylePath = (popularity?: ProductPopularity) => {
  switch (popularity) {
    case 'daily':
      return 'popularProductsSettings.titleStyle';
    case 'highlighted':
      return 'highlightedProductsSettings.titleStyle';
    case 'popular':
      return 'popularProductsSettings.titleStyle';
    default:
      return undefined;
  }
};

const getDividerStylePath = (popularity?: ProductPopularity) => {
  switch (popularity) {
    case 'daily':
      return 'popularProductsSettings.dividerStyle';
    case 'highlighted':
      return 'highlightedProductsSettings.dividerStyle';
    case 'popular':
      return 'popularProductsSettings.dividerStyle';
    default:
      return undefined;
  }
};

type ProductGridProps = {
  title?: string;
  products?: ProductSummary[];
  loading?: boolean;
  popularity?: ProductPopularity;
  maxCols?: number;
  titleStyle?: ComponentStyle;
  dividerStyle?: ComponentStyle;
};

export const ProductGrid = ({
  title,
  products,
  loading,
  popularity,
  maxCols = 6,
  titleStyle,
  dividerStyle,
}: ProductGridProps) => {
  const [isFetching, setIsFetching] = useState(false);
  const [fetchedProducts, setFetchedProducts] = useState<ProductSummary[]>([]);

  const { primary } = useThemeColor();

  useEffect(() => {
    if (popularity) {
      setIsFetching(true);
      getProductsByPopularity(popularity)
        .then((p) => setFetchedProducts(p))
        .catch((err) => toast.error(err.message))
        .finally(() => setIsFetching(false));
    }
  }, [popularity]);

  const minSize = 12 / maxCols;

  const itemSize = {
    xs: Math.max(minSize, 6),
    sm: Math.max(minSize, 4),
    md: Math.max(minSize, 3),
    lg: Math.max(minSize, 2),
  };

  const TitleCmp = (
    <Typography
      sx={{
        fontSize: { sx: '1.75rem', sm: '36px' },
        fontWeight: 600,
        ...titleStyle,
      }}
    >
      {title}
    </Typography>
  );

  const Divider = (
    <Box
      sx={{
        backgroundColor: primary,
        height: '3px',
        width: '100px',
        ...dividerStyle,
      }}
    />
  );

  const titleStylePath = getTitleStylePath(popularity);
  const dividerStylePath = getDividerStylePath(popularity);

  const resolvedProducts = products ?? fetchedProducts;
  const resolvedLoading = loading || isFetching;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        gap: 3,
        alignItems: 'center',
      }}
    >
      {!!title && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            alignItems: 'center',
          }}
        >
          {titleStylePath ? (
            <EditableStyleWrapper path={titleStylePath}>
              {TitleCmp}
            </EditableStyleWrapper>
          ) : (
            TitleCmp
          )}

          {dividerStylePath ? (
            <EditableStyleWrapper path={dividerStylePath}>
              {Divider}
            </EditableStyleWrapper>
          ) : (
            Divider
          )}
        </Box>
      )}

      <Grid container spacing={{ xs: 2, sm: 3, md: 4, lg: 5 }} width='100%'>
        {resolvedLoading
          ? Array(maxCols)
              .fill(null)
              .map((_, i) => (
                <Grid key={i} size={itemSize}>
                  <ProductCardPlaceholder />
                </Grid>
              ))
          : resolvedProducts.map((product) => (
              <Grid key={product.id} size={itemSize}>
                <ProductCard productSummary={product} />
              </Grid>
            ))}
      </Grid>
    </Box>
  );
};
